/**
 * Rótulos em português dos itens de navegação vindos dos plugins core.
 *
 * O portal é inteiramente em pt-BR, mas os itens que o Backstage traz de
 * fábrica chegam com o título em inglês: o `title` é um literal passado ao
 * `createFrontendPlugin` de cada plugin, não uma referência de tradução. O
 * resultado era "Catalog" e "Create" no mesmo menu que "Mapa de Repositórios" e
 * "Central de Auditoria".
 *
 * Por que um mapa e não o sistema de tradução do Backstage: o
 * `@backstage/frontend-plugin-api` de fato expõe `createTranslationRef` e um
 * `translationApiRef`, e os plugins trazem catálogos de mensagens — mas o
 * título do PLUGIN não passa por eles (é literal no `createFrontendPlugin`), e
 * nenhum catálogo pt-BR é distribuído junto. Migrar para tradução é um caminho
 * melhor no longo prazo e maior do que esta rodada.
 *
 * `page:api-docs` NÃO aparece aqui de propósito. O título dele é "APIs", que já
 * é a forma corrente em português — traduzir seria piorar.
 *
 * As chaves são os ids de extensão, os mesmos que o `Sidebar` já usa em
 * `nav.take(...)`, e vêm tipados de `item.node.spec.id`.
 */
export const PORTAL_NAV_LABELS: Readonly<Record<string, string>> = {
  'page:catalog': 'Catálogo',
  'page:scaffolder': 'Criar',
  'page:catalog-graph': 'Grafo do Catálogo',
  'page:app-visualizer': 'Visualizador da Aplicação',
  // O grupo que contém este item já se chama "Configurações"; a página é sobre
  // as preferências da própria pessoa (tema, idioma, perfil).
  'page:user-settings': 'Preferências',
};

/**
 * Ids que chegam da navegação e **não** entram no mapa, cada um por um motivo
 * escrito. Existe para que a ausência de uma chave seja uma decisão declarada,
 * e não um esquecimento — o fallback devolve o título original em silêncio, o
 * que faz um id faltante parecer que está tudo certo.
 *
 * LIMITE, escrito porque a ausência aqui é silenciosa: `navLabels.test.ts`
 * verifica a COERÊNCIA destas listas (nenhum id em duas, toda isenção com
 * motivo), e **não** a cobertura contra a navegação real. Não existe teste que
 * confronte estas listas com os ids que o `Sidebar` de fato usa — uma primeira
 * versão fazia isso lendo o código-fonte, e foi retirada porque este pacote
 * proíbe importar `fs`, com razão. Um id que entre na navegação e não apareça
 * em nenhuma destas listas continua caindo no fallback e aparecendo em inglês,
 * sem nada ficar vermelho. Fechar isso exige ver a navegação renderizada.
 */
export const PORTAL_NAV_ISENTOS: Readonly<Record<string, string>> = {
  'page:api-docs': 'O título é "APIs", que já é a forma corrente em português.',
};

/**
 * Páginas do próprio portal: o título é declarado em pt-BR no
 * `createFrontendPlugin` de cada módulo, então não passam por tradução.
 */
export const PORTAL_NAV_PROPRIOS: readonly string[] = [
  'page:repository-map',
  'page:ecosystem-map',
  'page:access-control',
  'page:delivery-policy-gate',
  'page:audit-center',
  'page:governance-alerts',
  'page:secret-scan',
  'page:runtime-config',
];

/**
 * Itens que o `Sidebar` retira da coleção e NÃO renderiza como item de menu.
 * Não têm rótulo visível, logo não precisam de tradução.
 */
export const PORTAL_NAV_NAO_RENDERIZADOS: readonly string[] = [
  'page:search',
  'page:notifications',
  'page:catalog-import',
];

/**
 * Devolve o rótulo em português de um item de navegação.
 *
 * Cai no título original quando o item não está mapeado — nenhum item pode
 * sumir ou ficar sem nome por causa de uma chave errada aqui. Itens do próprio
 * portal já nascem em pt-BR e passam direto por este fallback.
 */
export function portalNavLabel(id: string, tituloOriginal: string): string {
  return PORTAL_NAV_LABELS[id] ?? tituloOriginal;
}
